import React, { useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { WMTS, TileWMS, Vector as VectorSource } from 'ol/source';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { get as getProjection } from 'ol/proj';
import { getTopLeft } from 'ol/extent';
import { Draw, Modify, Select } from 'ol/interaction';
import { Fill, Stroke, Style, Circle, Text } from 'ol/style';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { click } from 'ol/events/condition';
import {
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
  Box,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material';
import {
  Layers as LayersIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Satellite as SatelliteIcon,
  Map as MapIcon,
  Palette as PaletteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';

// Configure proj4 for Dutch coordinate system
proj4.defs('EPSG:28992', '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +units=m +towgs84=565.2369,50.0087,465.658,-0.406857330322398,0.350732676542563,-1.8703473836068,4.0812 +no_defs');
register(proj4);

const MapComponent = ({ 
    interactionMode,
    drawingMode = 'new-area', // 'new-area', 'add-area', 'cut-hole'
    showCadastral, 
    showHouseNumbers,
    onLayerChange,
    boreholes, 
    boreholeToDraw, 
    onBoreholeDrawn, 
    onCancelBorehole, 
    onBoreholeSelected,
    centerOn,
    projectGeometry,
    onProjectGeometryUpdate,
    isBulkDrawing = false,
    importedLayers = [],
    onLayerFeatureClick,
    onToggleLayerVisibility,
    onRemoveImportedLayer,
    onToggleLayerExpanded,
    onToggleFeatureVisibility,
    expandedLayers = new Set(),
    featureVisibility = {},
    isFeatureVisible
}) => {

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const cadastralLayerRef = useRef(null);
    const drawInteractionRef = useRef(null);
    const modifyInteractionRef = useRef(null);
    const selectInteractionRef = useRef(null);
    const drawSourceRef = useRef(null);
    const highlightLayerRef = useRef(null);
    const boreholesLayerRef = useRef(null);
    const projectAreaLayerRef = useRef(null);
    const houseNumbersLayerRef = useRef(null);
    const aerialLayerRef = useRef(null);
    const bgtPastelLayerRef = useRef(null);
    const importedLayersRef = useRef([]);
    const interactionModeRef = useRef(interactionMode);
    const onProjectGeometryUpdateRef = useRef(onProjectGeometryUpdate);
    const boreholesToDrawRef = useRef(boreholeToDraw);
    const onBoreholeDrawnRef = useRef(onBoreholeDrawn);
    const onLayerFeatureClickRef = useRef(onLayerFeatureClick);
    
    // State for layers control
    const [layersOpen, setLayersOpen] = useState(true); // Start with layers panel open
    const [baseMapStyle, setBaseMapStyle] = useState('standard'); // 'standard', 'aerial', 'pastel'

    useEffect(() => {
        interactionModeRef.current = interactionMode;
    }, [interactionMode]);

    useEffect(() => {
        onProjectGeometryUpdateRef.current = onProjectGeometryUpdate;
    }, [onProjectGeometryUpdate]);

    useEffect(() => {
        boreholesToDrawRef.current = boreholeToDraw;
    }, [boreholeToDraw]);

    useEffect(() => {
        onBoreholeDrawnRef.current = onBoreholeDrawn;
    }, [onBoreholeDrawn]);

    useEffect(() => {
        if (centerOn && mapInstance.current) {
            mapInstance.current.getView().animate({
                center: centerOn,
                zoom: 13,
                duration: 1500
            });
        }
    }, [centerOn]);

    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const projection = getProjection('EPSG:28992');
        projection.setExtent([-285401.92, 22598.08, 595401.92, 903401.92]);
        const projectionExtent = projection.getExtent();
        const resolutions = [3440.64, 1720.32, 860.16, 430.08, 215.04, 107.52, 53.76, 26.88, 13.44, 6.72, 3.36, 1.68, 0.84, 0.42, 0.21];
        const matrixIds = resolutions.map((_, i) => `EPSG:28992:${i}`);

        // Base layer - BRT achtergrondkaart (standard)
        const brtSource = new WMTS({
            url: '/service/brt/achtergrondkaart/wmts/v2_0',
            layer: 'standaard',
            matrixSet: 'EPSG:28992',
            format: 'image/png',
            projection,
            tileGrid: new WMTSTileGrid({
                origin: getTopLeft(projectionExtent),
                resolutions,
                matrixIds
            })
        });
        const baseLayer = new TileLayer({ 
            source: brtSource, 
            zIndex: 0,
            visible: baseMapStyle === 'standard'
        });

        // Aerial imagery layer - using the dedicated luchtfoto service
        const aerialSource = new WMTS({
            url: '/service/hwh/luchtfotorgb/wmts/v1_0',
            layer: 'Actueel_ortho25',
            matrixSet: 'EPSG:28992',
            format: 'image/jpeg',
            projection,
            tileGrid: new WMTSTileGrid({
                origin: getTopLeft(projectionExtent),
                resolutions,
                matrixIds
            })
        });
        aerialLayerRef.current = new TileLayer({ 
            source: aerialSource, 
            zIndex: 0,
            visible: baseMapStyle === 'aerial'
        });

        // BGT Pastel layer
        const bgtPastelSource = new WMTS({
            url: '/service/brt/achtergrondkaart/wmts/v2_0',
            layer: 'pastel',
            matrixSet: 'EPSG:28992',
            format: 'image/png',
            projection,
            tileGrid: new WMTSTileGrid({
                origin: getTopLeft(projectionExtent),
                resolutions,
                matrixIds
            })
        });
        bgtPastelLayerRef.current = new TileLayer({ 
            source: bgtPastelSource, 
            zIndex: 0,
            visible: baseMapStyle === 'pastel'
        });
        
        // Cadastral layer - Kadaster
        const wmsKadasterSource = new TileWMS({
            url: '/service/kadaster/kadastralekaart/wms/v5_0',
            params: {
                'LAYERS': 'bebouwing,perceel',
                'TILED': true,
                'VERSION': '1.3.0',
                'SRS': 'EPSG:28992'
            }
        });
        cadastralLayerRef.current = new TileLayer({
            source: wmsKadasterSource,
            zIndex: 2,
            visible: showCadastral
        });
        
        // Drawing layer
        drawSourceRef.current = new VectorSource();
        const drawLayer = new VectorLayer({
            source: drawSourceRef.current,
            style: new Style({
                fill: new Fill({ color: 'rgba(255, 0, 0, 0.2)' }),
                stroke: new Stroke({ color: '#ff0000', width: 2 })
            }),
            zIndex: 4
        });

        // Project area layer with support for multi-polygon and holes
        const projectAreaSource = new VectorSource();
        projectAreaLayerRef.current = new VectorLayer({
            source: projectAreaSource,
            style: (feature) => {
                const geometry = feature.getGeometry();
                const isMultiPolygon = geometry.getType() === 'MultiPolygon';
                const hasHoles = geometry.getType() === 'Polygon' && geometry.getLinearRings().length > 1;
                
                return new Style({
                    fill: new Fill({ 
                        color: hasHoles ? 'rgba(0, 100, 255, 0.05)' : 'rgba(0, 100, 255, 0.1)' 
                    }),
                    stroke: new Stroke({ 
                        color: isMultiPolygon ? '#ff6600' : '#0064ff', 
                        width: hasHoles ? 2 : 3, 
                        lineDash: isMultiPolygon ? [8, 4] : [4, 8] 
                    }),
                });
            },
            zIndex: 1, 
        });

        // Boreholes layer
        const boreholesSource = new VectorSource();
        boreholesLayerRef.current = new VectorLayer({
            source: boreholesSource,
            style: (feature) => {
                const name = feature.get('name') || '';
                return new Style({
                    image: new Circle({
                        radius: 8,
                        fill: new Fill({ color: '#3498db' }),
                        stroke: new Stroke({ color: 'white', width: 2 }),
                    }),
                    text: new Text({
                        text: name,
                        font: '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        fill: new Fill({ color: '#2c3e50' }),
                        stroke: new Stroke({ color: 'white', width: 3 }),
                        offsetY: -25,
                        textAlign: 'center',
                        textBaseline: 'middle',
                        backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.9)' }),
                        backgroundStroke: new Stroke({ color: '#bdc3c7', width: 1 }),
                        padding: [4, 8, 4, 8],
                    }),
                });
            },
            zIndex: 5,
        });

        // Highlight layer
        const highlightSource = new VectorSource();
        highlightLayerRef.current = new VectorLayer({
            source: highlightSource,
            style: new Style({
                stroke: new Stroke({ color: '#00ffff', width: 3 }),
                fill: new Fill({ color: 'rgba(0, 255, 255, 0.3)' }),
            }),
            zIndex: 3,
        });

        // House numbers layer - temporarily disabled due to service unavailability
        // const houseNumbersSource = new TileWMS({
        //     url: '/service/bag/wms/v1_0',
        //     params: {
        //         'LAYERS': 'adressen',
        //         'TILED': true,
        //         'VERSION': '1.3.0',
        //         'SRS': 'EPSG:28992'
        //     }
        // });
        houseNumbersLayerRef.current = new TileLayer({
            source: new VectorSource(), // Empty source
            zIndex: 6,
            visible: false, // Always invisible
        });

        // Initialize map with all layers
        const initialCenterCoords = proj4('EPSG:4326', 'EPSG:28992', [5.2064572, 52.0609095]);

        mapInstance.current = new Map({
            target: mapRef.current,
            layers: [
                baseLayer,
                aerialLayerRef.current,
                bgtPastelLayerRef.current,
                cadastralLayerRef.current,
                projectAreaLayerRef.current,
                highlightLayerRef.current,
                drawLayer,
                boreholesLayerRef.current,
                houseNumbersLayerRef.current
            ],
            view: new View({
                projection,
                center: initialCenterCoords,
                zoom: 14
            })
        });

        // Enhanced map click handler with imported layers and cadastral fallback
        mapInstance.current.on('singleclick', (evt) => {
            const currentMode = interactionModeRef.current;
            const currentBoreholeToDraw = boreholesToDrawRef.current;
            
            if ((currentMode === 'draw-borehole' || currentMode === 'select-borehole') && currentBoreholeToDraw) {
                // Draw or select borehole position
                if (onBoreholeDrawnRef.current) {
                    onBoreholeDrawnRef.current(evt.coordinate);
                }
                return;
            }
            
            // Only handle clicks in info mode or select-project-plot mode
            if (currentMode !== 'info' && currentMode !== 'select-project-plot') {
                return;
            }
            
            // Clear previous highlights
            highlightLayerRef.current.getSource().clear();
            
            // First priority: Check for clicks on imported layers
            const pixelFeatures = mapInstance.current.getFeaturesAtPixel(evt.pixel);
            
            const importedFeature = pixelFeatures.find(feature => 
                feature.get('layerId') && feature.get('originalFeature')
            );
            
            if (importedFeature) {
                if (currentMode === 'info' && onLayerFeatureClickRef.current) {
                    // Info mode: just show info or handle as needed
                    const originalFeature = importedFeature.get('originalFeature');
                    onLayerFeatureClickRef.current(originalFeature);
                    return;
                } else if (currentMode === 'select-project-plot') {
                    // Select mode: use the imported feature geometry for project
                    const originalFeature = importedFeature.get('originalFeature');
                    
                    // Highlight the selected imported feature
                    const geoJsonFormat = new GeoJSON();
                    const highlightFeature = geoJsonFormat.readFeature(originalFeature, {
                        dataProjection: 'EPSG:28992',
                        featureProjection: 'EPSG:28992'
                    });
                    
                    highlightLayerRef.current.getSource().clear();
                    highlightLayerRef.current.getSource().addFeature(highlightFeature);
                    
                    // Update project geometry
                    if (onProjectGeometryUpdateRef.current) {
                        onProjectGeometryUpdateRef.current(originalFeature.geometry);
                    }
                    return;
                }
            }
            
            // Second priority: Cadastral plot selection (fallback)
            if (!cadastralLayerRef.current.getVisible()) {
                return;
            }
            
            const viewResolution = mapInstance.current.getView().getResolution();
            const url = wmsKadasterSource.getFeatureInfoUrl(
                evt.coordinate, 
                viewResolution, 
                'EPSG:28992', 
                { 
                    'INFO_FORMAT': 'application/json', 
                    'FEATURE_COUNT': 1,
                    'QUERY_LAYERS': 'perceel',
                    'LAYERS': 'perceel'
                }
            );
            
            if (url) {
                // Try proxy first, fallback to direct URL if needed
                const tryFetch = async (fetchUrl) => {
                    try {
                        const response = await fetch(fetchUrl);
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return await response.json();
                    } catch (proxyError) {
                        console.warn('Proxy failed, trying direct URL:', proxyError);
                        // Convert proxy URL to direct URL
                        const directUrl = fetchUrl.replace('/service/', 'https://service.pdok.nl/');
                        const directResponse = await fetch(directUrl);
                        if (!directResponse.ok) {
                            throw new Error(`Direct URL failed with status: ${directResponse.status}`);
                        }
                        return await directResponse.json();
                    }
                };

                tryFetch(url)
                    .then(data => {
                        if (data.features && data.features.length > 0) {
                            const perceelID = data.features[0].properties.identificatieLokaalID;
                            if (perceelID && currentMode === 'select-project-plot') {
                                fetchPerceelGeometry(perceelID);
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Error fetching cadastral info:', err);
                    });
            }
        });

        return () => {
            if (mapInstance.current) {
                mapInstance.current.setTarget(null);
                mapInstance.current = null;
            }
        };
    }, []);

    // Fetch geometry for selected cadastral plot
    const fetchPerceelGeometry = async (perceelID) => {
        try {
            const filter = `<Filter><PropertyIsEqualTo><PropertyName>identificatieLokaalID</PropertyName><Literal>${perceelID}</Literal></PropertyIsEqualTo></Filter>`;
            
            // Try proxy first, fallback to direct URL if proxy fails
            let wfsUrl = `/service/kadaster/kadastralekaart/wfs/v5_0?service=WFS&version=2.0.0&request=GetFeature&typeName=kadastralekaartv5:perceel&outputFormat=application/json&filter=${encodeURIComponent(filter)}`;
            
            let response;
            try {
                response = await fetch(wfsUrl);
                if (!response.ok) {
                    throw new Error(`Proxy failed with status: ${response.status}`);
                }
            } catch (proxyError) {
                console.warn('Proxy failed, trying direct URL:', proxyError);
                // Fallback to direct URL
                wfsUrl = `https://service.pdok.nl/kadaster/kadastralekaart/wfs/v5_0?service=WFS&version=2.0.0&request=GetFeature&typeName=kadastralekaartv5:perceel&outputFormat=application/json&filter=${encodeURIComponent(filter)}`;
                response = await fetch(wfsUrl);
                if (!response.ok) {
                    throw new Error(`Direct URL failed with status: ${response.status}`);
                }
            }
            
            const data = await response.json();
            
            if (data.features && data.features.length > 0) {
                const feature = data.features[0];
                const geometry = feature.geometry;
                
                // Highlight the selected plot
                const geoJsonFormat = new GeoJSON();
                const olFeature = geoJsonFormat.readFeature(feature, {
                    dataProjection: 'EPSG:28992',
                    featureProjection: 'EPSG:28992'
                });
                
                highlightLayerRef.current.getSource().clear();
                highlightLayerRef.current.getSource().addFeature(olFeature);
                
                // Update project geometry
                if (onProjectGeometryUpdateRef.current) {
                    onProjectGeometryUpdateRef.current(geometry);
                }
            }
        } catch (error) {
            console.error('Error fetching plot geometry:', error);
        }
    };

    // Update cadastral layer visibility
    useEffect(() => {
        if (cadastralLayerRef.current) {
            cadastralLayerRef.current.setVisible(showCadastral);
        }
    }, [showCadastral]);

    // Update house numbers layer visibility - disabled
    useEffect(() => {
        if (houseNumbersLayerRef.current) {
            houseNumbersLayerRef.current.setVisible(false); // Always false
        }
    }, [showHouseNumbers]);

    // Update boreholes on map
    useEffect(() => {
        if (!boreholesLayerRef.current) return;

        const source = boreholesLayerRef.current.getSource();
        source.clear();

        boreholes.forEach(borehole => {
            if (borehole.geometry && typeof borehole.geometry === 'string') {
                const parts = borehole.geometry.trim().split(/\s+/);
                if (parts.length >= 2) {
                    const x = parseFloat(parts[0]);
                    const y = parseFloat(parts[1]);
                    
                    if (!isNaN(x) && !isNaN(y)) {
                        const feature = new Feature({
                            geometry: new Point([x, y]),
                            name: borehole.name,
                            borehole: borehole
                        });
                        source.addFeature(feature);
                    }
                }
            }
        });
    }, [boreholes]);

    // Update project geometry on map
    useEffect(() => {
        if (!projectAreaLayerRef.current) return;

        const source = projectAreaLayerRef.current.getSource();
        source.clear();

        if (projectGeometry) {
            try {
                const geoJsonFormat = new GeoJSON();
                
                // Handle both Polygon and MultiPolygon geometries
                let geometry = projectGeometry;
                if (Array.isArray(projectGeometry)) {
                    // Convert array of geometries to MultiPolygon
                    geometry = {
                        type: 'MultiPolygon',
                        coordinates: projectGeometry.map(geom => geom.coordinates)
                    };
                }
                
                const feature = geoJsonFormat.readFeature({
                    type: 'Feature',
                    geometry: geometry
                }, {
                    dataProjection: 'EPSG:28992',
                    featureProjection: 'EPSG:28992'
                });
                source.addFeature(feature);
            } catch (error) {
                console.error('Error adding project geometry to map:', error);
            }
        }
    }, [projectGeometry, baseMapStyle]);

    // Auto-zoom to project contour, boreholes or imported layers on initial load
    useEffect(() => {
        if (!mapInstance.current || isBulkDrawing) return;

        const autoZoomToContent = () => {
            const map = mapInstance.current;
            const view = map.getView();

            // Priority 1: Project geometry
            if (projectGeometry && projectAreaLayerRef.current) {
                const source = projectAreaLayerRef.current.getSource();
                if (source.getFeatures().length > 0) {
                    const extent = source.getExtent();
                    view.fit(extent, {
                        padding: [50, 50, 50, 50],
                        maxZoom: 16,
                        duration: 1000
                    });
                    return;
                }
            }

            // Priority 2: Imported layers
            if (importedLayersRef.current.length > 0) {
                let combinedExtent = null;
                
                importedLayersRef.current.forEach((layer, index) => {
                    const source = layer.getSource();
                    if (source.getFeatures().length > 0) {
                        const layerExtent = source.getExtent();
                        
                        if (combinedExtent) {
                            // Extend the existing extent
                            combinedExtent[0] = Math.min(combinedExtent[0], layerExtent[0]);
                            combinedExtent[1] = Math.min(combinedExtent[1], layerExtent[1]);
                            combinedExtent[2] = Math.max(combinedExtent[2], layerExtent[2]);
                            combinedExtent[3] = Math.max(combinedExtent[3], layerExtent[3]);
                        } else {
                            combinedExtent = [...layerExtent];
                        }
                    }
                });
                
                if (combinedExtent) {
                    view.fit(combinedExtent, {
                        padding: [50, 50, 50, 50],
                        maxZoom: 16,
                        duration: 1000
                    });
                    return;
                }
            }

            // Priority 3: Boreholes
            if (boreholes.length > 0 && boreholesLayerRef.current) {
                const source = boreholesLayerRef.current.getSource();
                if (source.getFeatures().length > 0) {
                    const extent = source.getExtent();
                    view.fit(extent, {
                        padding: [100, 100, 100, 100],
                        maxZoom: 18,
                        duration: 1000
                    });
                    return;
                }
            }
        };

        // Small delay to ensure layers are loaded
        const timeoutId = setTimeout(autoZoomToContent, 500);
        
        return () => clearTimeout(timeoutId);
    }, [projectGeometry, boreholes, isBulkDrawing, importedLayers]); // Also trigger on imported layers change

    // Update base map style visibility
    useEffect(() => {
        if (!mapInstance.current) return;

        // Update base layer visibility
        mapInstance.current.getLayers().forEach(layer => {
            if (layer.getZIndex() === 0) {
                if (layer === aerialLayerRef.current) {
                    layer.setVisible(baseMapStyle === 'aerial');
                } else if (layer === bgtPastelLayerRef.current) {
                    layer.setVisible(baseMapStyle === 'pastel');
                } else {
                    layer.setVisible(baseMapStyle === 'standard');
                }
            }
        });
    }, [baseMapStyle]);

    // Enhanced drawing interactions with multi-polygon and modify support
    useEffect(() => {
        if (!mapInstance.current) return;

        // Remove existing interactions
        if (drawInteractionRef.current) {
            mapInstance.current.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
        }
        if (modifyInteractionRef.current) {
            mapInstance.current.removeInteraction(modifyInteractionRef.current);
            modifyInteractionRef.current = null;
        }
        if (selectInteractionRef.current) {
            mapInstance.current.removeInteraction(selectInteractionRef.current);
            selectInteractionRef.current = null;
        }

        if (interactionMode === 'draw-project-area') {
            const drawInteraction = new Draw({
                source: drawSourceRef.current,
                type: 'Polygon',
                style: new Style({
                    fill: new Fill({ 
                        color: drawingMode === 'cut-hole' ? 'rgba(255, 165, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)' 
                    }),
                    stroke: new Stroke({ 
                        color: drawingMode === 'cut-hole' ? '#ff8800' : '#ff0000', 
                        width: 2,
                        lineDash: drawingMode === 'cut-hole' ? [5, 5] : undefined
                    })
                })
            });

            drawInteraction.on('drawend', (event) => {
                const feature = event.feature;
                const geometry = feature.getGeometry();
                
                // Convert to GeoJSON
                const geoJsonFormat = new GeoJSON();
                const geoJsonGeometry = geoJsonFormat.writeGeometryObject(geometry, {
                    dataProjection: 'EPSG:28992',
                    featureProjection: 'EPSG:28992'
                });

                if (onProjectGeometryUpdateRef.current) {
                    // Pass the drawing mode to the handler
                    onProjectGeometryUpdateRef.current(geoJsonGeometry, drawingMode);
                }

                // Clear the draw source
                setTimeout(() => {
                    drawSourceRef.current.clear();
                }, 100);
            });

            mapInstance.current.addInteraction(drawInteraction);
            drawInteractionRef.current = drawInteraction;
        } else if (interactionMode === 'modify-project-area' && projectGeometry) {
            // Add modify interaction for existing project geometry
            const selectInteraction = new Select({
                layers: [projectAreaLayerRef.current],
                condition: click
            });

            const modifyInteraction = new Modify({
                features: selectInteraction.getFeatures(),
                style: new Style({
                    fill: new Fill({ color: 'rgba(255, 255, 0, 0.2)' }),
                    stroke: new Stroke({ color: '#ffff00', width: 3 })
                })
            });

            modifyInteraction.on('modifyend', (event) => {
                const features = event.features.getArray();
                if (features.length > 0) {
                    const feature = features[0];
                    const geometry = feature.getGeometry();
                    
                    const geoJsonFormat = new GeoJSON();
                    const geoJsonGeometry = geoJsonFormat.writeGeometryObject(geometry, {
                        dataProjection: 'EPSG:28992',
                        featureProjection: 'EPSG:28992'
                    });

                    if (onProjectGeometryUpdateRef.current) {
                        onProjectGeometryUpdateRef.current(geoJsonGeometry, 'modify');
                    }
                }
            });

            mapInstance.current.addInteraction(selectInteraction);
            mapInstance.current.addInteraction(modifyInteraction);
            selectInteractionRef.current = selectInteraction;
            modifyInteractionRef.current = modifyInteraction;
        }

        return () => {
            if (drawInteractionRef.current) {
                mapInstance.current.removeInteraction(drawInteractionRef.current);
                drawInteractionRef.current = null;
            }
            if (modifyInteractionRef.current) {
                mapInstance.current.removeInteraction(modifyInteractionRef.current);
                modifyInteractionRef.current = null;
            }
            if (selectInteractionRef.current) {
                mapInstance.current.removeInteraction(selectInteractionRef.current);
                selectInteractionRef.current = null;
            }
        };
    }, [interactionMode, projectGeometry, drawingMode]);

    useEffect(() => {
        onLayerFeatureClickRef.current = onLayerFeatureClick;
    }, [onLayerFeatureClick]);

    // Update imported layers on map
    useEffect(() => {
        if (!mapInstance.current) {
            return;
        }

        // Remove existing imported layers
        importedLayersRef.current.forEach((layer, index) => {
            if (mapInstance.current.getLayers().getArray().includes(layer)) {
                mapInstance.current.removeLayer(layer);
            }
        });
        importedLayersRef.current = [];

        // Add new imported layers
        importedLayers.forEach((layerData, index) => {
            if (!layerData.visible) {
                return;
            }

            const vectorSource = new VectorSource();
            const geoJsonFormat = new GeoJSON();

            // Add features to the source (only visible ones)
            layerData.features.forEach((feature, featureIndex) => {
                try {
                    // Check if this specific feature is visible
                    const featureVisible = isFeatureVisible ? isFeatureVisible(layerData.id, featureIndex) : true;
                    
                    if (!featureVisible) {
                        return;
                    }
                    
                    // Check if coordinates are already in Dutch RD (EPSG:28992) format
                    const firstCoord = feature.geometry?.coordinates?.[0]?.[0];
                    const isAlreadyRD = firstCoord && firstCoord[0] > 10000 && firstCoord[0] < 300000;
                    
                    const olFeature = geoJsonFormat.readFeature(feature, {
                        dataProjection: isAlreadyRD ? 'EPSG:28992' : 'EPSG:4326',
                        featureProjection: 'EPSG:28992'
                    });
                    
                    // Add layer metadata to the feature
                    olFeature.set('layerId', layerData.id);
                    olFeature.set('layerName', layerData.name);
                    olFeature.set('originalFeature', feature);
                    olFeature.set('featureIndex', featureIndex);
                    
                    vectorSource.addFeature(olFeature);
                } catch (error) {
                    console.error(`Error adding feature ${featureIndex} to imported layer:`, error, feature);
                }
            });

            // Create layer with styling
            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: createImportedLayerStyle(layerData),
                zIndex: 10 + index, // Higher z-index for imported layers
            });

            // Add click handler for imported features
            vectorLayer.on('click', (evt) => {
                const feature = evt.feature;
                if (feature && onLayerFeatureClickRef.current) {
                    const originalFeature = feature.get('originalFeature');
                    if (originalFeature) {
                        onLayerFeatureClickRef.current(originalFeature);
                    }
                }
            });

            mapInstance.current.addLayer(vectorLayer);
            importedLayersRef.current.push(vectorLayer);
        });
    }, [importedLayers, featureVisibility]);

    // Helper function to create styles for imported layers
    const createImportedLayerStyle = (layerData) => {
        const color = layerData.color || '#3498db';
        const rgbColor = hexToRgb(color);
        
        return new Style({
            fill: new Fill({ 
                color: `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 0.3)` 
            }),
            stroke: new Stroke({ 
                color: color, 
                width: 2 
            }),
            image: new Circle({
                radius: 6,
                fill: new Fill({ color: color }),
                stroke: new Stroke({ color: 'white', width: 2 }),
            })
        });
    };

    // Helper function to convert hex to RGB
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 52, g: 152, b: 219 }; // Default blue
    };

    return (
        <div 
            ref={mapRef} 
            style={{ 
                width: '100%', 
                height: '100%',
                cursor: (interactionMode === 'draw-borehole' || interactionMode === 'select-borehole' || interactionMode === 'draw-project-area' || interactionMode === 'modify-project-area' || interactionMode === 'select-project-plot') ? 
                    (drawingMode === 'cut-hole' ? 'not-allowed' : 'crosshair') : 'default',
                position: 'relative'
            }} 
        >
            {/* Floating Layers Control */}
            <Paper
                elevation={4}
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                    width: layersOpen ? 340 : 48, // Increased width for imported layers
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
                }}
            >
                {!layersOpen ? (
                    // Collapsed state - just the icon
                    <Box sx={{ p: 1 }}>
                        <Tooltip title="Kaartlagen openen">
                            <IconButton
                                size="small"
                                onClick={() => setLayersOpen(true)}
                            >
                                <LayersIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    // Expanded state - full panel
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <LayersIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                Kaartlagen
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setLayersOpen(false)}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        
                        <Box sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Basiskaart
                            </Typography>
                            <ToggleButtonGroup
                                value={baseMapStyle}
                                exclusive
                                onChange={(e, value) => value && setBaseMapStyle(value)}
                                size="small"
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="standard" sx={{ flex: 1 }}>
                                    <Tooltip title="Standaard kaart">
                                        <MapIcon />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="aerial" sx={{ flex: 1 }}>
                                    <Tooltip title="Luchtfoto">
                                        <SatelliteIcon />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="pastel" sx={{ flex: 1 }}>
                                    <Tooltip title="BGT Pastel">
                                        <PaletteIcon />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Divider sx={{ my: 1 }} />
                            
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Overlays
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showCadastral}
                                            onChange={(e) => onLayerChange?.setCadastral(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Kadastrale kaart"
                                    sx={{ mb: 0.5 }}
                                />
                            </Box>

                            {/* Imported Layers Section */}
                            {importedLayers.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        Geïmporteerde Lagen ({importedLayers.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {importedLayers.map((layer) => {
                                            const isExpanded = expandedLayers.has(layer.id);
                                                
                                                return (
                                                    <Accordion 
                                                        key={layer.id}
                                                        expanded={isExpanded}
                                                        onChange={() => onToggleLayerExpanded?.(layer.id)}
                                                        sx={{
                                                            boxShadow: 1,
                                                            '&.MuiAccordion-root': {
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                '&:before': {
                                                                    display: 'none',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <AccordionSummary 
                                                            expandIcon={<ExpandMoreIcon />}
                                                            sx={{ 
                                                                minHeight: 48,
                                                                '& .MuiAccordionSummary-content': {
                                                                    alignItems: 'center',
                                                                    my: 0.5
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 12,
                                                                        height: 12,
                                                                        backgroundColor: layer.color,
                                                                        borderRadius: '50%',
                                                                        border: '1px solid white',
                                                                        boxShadow: 1,
                                                                        mr: 1.5,
                                                                        flexShrink: 0
                                                                    }}
                                                                />
                                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {layer.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {layer.features.length} locatie(s) - {layer.type}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                                                    <Tooltip title={layer.visible ? 'Verberg alle' : 'Toon alle'}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                onToggleLayerVisibility?.(layer.id);
                                                                            }}
                                                                        >
                                                                            {layer.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Verwijder laag">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                onRemoveImportedLayer?.(layer.id);
                                                                            }}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails sx={{ pt: 0 }}>
                                                            <List dense disablePadding>
                                                                {layer.features.map((feature, featureIndex) => {
                                                                    const featureVisible = isFeatureVisible ? isFeatureVisible(layer.id, featureIndex) : true;
                                                                    const featureName = feature.properties?.NAAM || 
                                                                                       feature.properties?.name || 
                                                                                       feature.properties?.Name ||
                                                                                       `Locatie ${featureIndex + 1}`;
                                                                    
                                                                    return (
                                                                        <ListItem 
                                                                            key={featureIndex}
                                                                            sx={{ 
                                                                                pl: 0, 
                                                                                pr: 0,
                                                                                borderRadius: 1,
                                                                                mb: 0.5,
                                                                                bgcolor: featureVisible ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                                                                opacity: featureVisible ? 1 : 0.6
                                                                            }}
                                                                        >
                                                                            <LocationOnIcon 
                                                                                sx={{ 
                                                                                    mr: 1, 
                                                                                    color: featureVisible ? layer.color : 'text.disabled',
                                                                                    fontSize: 16 
                                                                                }} 
                                                                            />
                                                                            <ListItemText 
                                                                                primary={featureName}
                                                                                primaryTypographyProps={{
                                                                                    variant: 'body2',
                                                                                    sx: { fontWeight: featureVisible ? 500 : 400 }
                                                                                }}
                                                                                secondary={
                                                                                    feature.properties?.STRAAT && feature.properties?.HNR1 ? 
                                                                                        `${feature.properties.STRAAT} ${feature.properties.HNR1}` :
                                                                                        `${feature.geometry?.type || 'Onbekend'}`
                                                                                }
                                                                                secondaryTypographyProps={{
                                                                                    variant: 'caption',
                                                                                    color: 'text.secondary'
                                                                                }}
                                                                            />
                                                                            <ListItemSecondaryAction>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => onToggleFeatureVisibility?.(layer.id, featureIndex)}
                                                                                >
                                                                                    {featureVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                                                </IconButton>
                                                                            </ListItemSecondaryAction>
                                                                        </ListItem>
                                                                    );
                                                                })}
                                                            </List>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                                                                    );
                                                })}
                                        </Box>
                                </>
                            )}
                        </Box>
                    </>
                )}
            </Paper>
        </div>
    );
};

export default MapComponent; 